import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Table, X } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const SecuredAssetsTablePage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({
        branch: '',
        state: '',
        borrower_name: '',
        guarantor_name_1: '',
        guarantor_name_2: '',
        guarantor_name_3: '',
        registered_address_borrower: '',
        registered_address_guarantor_1: '',
        registered_address_guarantor_2: '',
        registered_address_guarantor_3: '',
        outstanding_amount: '',
        asset_classification: '',
        date_of_asset_classification: '',
        details_of_security_possessed: '',
        name_of_title_holder: '',
        display_order: 0,
        is_active: true
    });
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/secured-assets-table');
            if (response.data.success) {
                setRows(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching table data:', error);
            toast.error('Failed to load table data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (row) => {
        setEditingRow(row);
        setFormData({
            branch: row.branch || '',
            state: row.state || '',
            borrower_name: row.borrower_name || '',
            guarantor_name_1: row.guarantor_name_1 || '',
            guarantor_name_2: row.guarantor_name_2 || '',
            guarantor_name_3: row.guarantor_name_3 || '',
            registered_address_borrower: row.registered_address_borrower || '',
            registered_address_guarantor_1: row.registered_address_guarantor_1 || '',
            registered_address_guarantor_2: row.registered_address_guarantor_2 || '',
            registered_address_guarantor_3: row.registered_address_guarantor_3 || '',
            outstanding_amount: row.outstanding_amount || '',
            asset_classification: row.asset_classification || '',
            date_of_asset_classification: row.date_of_asset_classification || '',
            details_of_security_possessed: row.details_of_security_possessed || '',
            name_of_title_holder: row.name_of_title_holder || '',
            display_order: row.display_order,
            is_active: row.is_active === 1 || row.is_active === true
        });
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingRow(null);
        setFormData({
            branch: '',
            state: '',
            borrower_name: '',
            guarantor_name_1: '',
            guarantor_name_2: '',
            guarantor_name_3: '',
            registered_address_borrower: '',
            registered_address_guarantor_1: '',
            registered_address_guarantor_2: '',
            registered_address_guarantor_3: '',
            outstanding_amount: '',
            asset_classification: '',
            date_of_asset_classification: '',
            details_of_security_possessed: '',
            name_of_title_holder: '',
            display_order: rows.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingRow) {
                await axios.put(`/api/admin/secured-assets-table/${editingRow.id}`, formData);
                toast.success('Row updated successfully');
            } else {
                await axios.post('/api/admin/secured-assets-table', formData);
                toast.success('Row added successfully');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving row:', error);
            toast.error('Failed to save row');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/secured-assets-table/${deleteConfirm.id}`);
            toast.success('Row deleted successfully');
            setDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting row:', error);
            toast.error('Failed to delete row');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Secured Assets Table Data</h1>
                    <p className="text-slate-500 mt-1">Manage the detailed information on secured assets</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Entry
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12">Loading...</div>
            ) : rows.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
                    <Table className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-800 mb-2">No data available</h3>
                    <p className="text-slate-500">Add entries to populate the table.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-[10px] min-w-[2000px]">
                            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                                <tr>
                                    <th className="p-2 border">Branch</th>
                                    <th className="p-2 border w-[120px]">State</th>
                                    <th className="p-2 border">Borrower</th>
                                    <th className="p-2 border">Guarantor 1</th>
                                    <th className="p-2 border">Guarantor 2</th>
                                    <th className="p-2 border">Guarantor 3</th>
                                    <th className="p-2 border w-[200px]">Borrower Addr</th>
                                    <th className="p-2 border w-[200px]">G1 Addr</th>
                                    <th className="p-2 border w-[200px]">G2 Addr</th>
                                    <th className="p-2 border w-[200px]">G3 Addr</th>
                                    <th className="p-2 border">Outstanding</th>
                                    <th className="p-2 border">Class.</th>
                                    <th className="p-2 border">Date</th>
                                    <th className="p-2 border w-[200px]">Security Details</th>
                                    <th className="p-2 border">Title Holder</th>
                                    <th className="p-2 border text-center">Status</th>
                                    <th className="p-3 text-right sticky right-0 bg-slate-50 z-10 border-l shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.05)]">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-2 border font-medium text-slate-800">{row.branch}</td>
                                        <td className="p-2 border text-slate-500 whitespace-nowrap">{row.state}</td>
                                        <td className="p-2 border font-bold text-primary-700">{row.borrower_name}</td>
                                        <td className="p-2 border text-slate-600">{row.guarantor_name_1 || '-'}</td>
                                        <td className="p-2 border text-slate-600">{row.guarantor_name_2 || '-'}</td>
                                        <td className="p-2 border text-slate-600">{row.guarantor_name_3 || '-'}</td>
                                        <td className="p-2 border text-[9px] leading-tight max-w-[200px] truncate" title={row.registered_address_borrower}>{row.registered_address_borrower || '-'}</td>
                                        <td className="p-2 border text-[9px] leading-tight max-w-[200px] truncate" title={row.registered_address_guarantor_1}>{row.registered_address_guarantor_1 || '-'}</td>
                                        <td className="p-2 border text-[9px] leading-tight max-w-[200px] truncate" title={row.registered_address_guarantor_2}>{row.registered_address_guarantor_2 || '-'}</td>
                                        <td className="p-2 border text-[9px] leading-tight max-w-[200px] truncate" title={row.registered_address_guarantor_3}>{row.registered_address_guarantor_3 || '-'}</td>
                                        <td className="p-2 border font-bold text-slate-700">{row.outstanding_amount}</td>
                                        <td className="p-2 border font-semibold text-red-600">{row.asset_classification}</td>
                                        <td className="p-2 border whitespace-nowrap">{row.date_of_asset_classification}</td>
                                        <td className="p-2 border text-[9px] leading-tight max-w-[200px] truncate" title={row.details_of_security_possessed}>{row.details_of_security_possessed || '-'}</td>
                                        <td className="p-2 border font-medium italic">{row.name_of_title_holder || '-'}</td>
                                        <td className="p-2 border text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                {row.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="p-3 text-right sticky right-0 bg-white group-hover:bg-slate-50 z-10 border-l shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.05)]">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => handleEdit(row)} className="p-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => handleDelete(row.id)} className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-slate-800">{editingRow ? 'Edit Entry' : 'Add New Entry'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[75vh] overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Basic Info */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Branch Name</label>
                                    <input type="text" value={formData.branch} onChange={(e) => setFormData({ ...formData, branch: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">State</label>
                                    <input type="text" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Borrower Name</label>
                                    <input type="text" required value={formData.borrower_name} onChange={(e) => setFormData({ ...formData, borrower_name: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>

                                {/* Guarantors */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Guarantor 1</label>
                                    <input type="text" value={formData.guarantor_name_1} onChange={(e) => setFormData({ ...formData, guarantor_name_1: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Guarantor 2</label>
                                    <input type="text" value={formData.guarantor_name_2} onChange={(e) => setFormData({ ...formData, guarantor_name_2: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Guarantor 3</label>
                                    <input type="text" value={formData.guarantor_name_3} onChange={(e) => setFormData({ ...formData, guarantor_name_3: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>

                                {/* Addresses */}
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Borrower Registered Address</label>
                                    <textarea rows="2" value={formData.registered_address_borrower} onChange={(e) => setFormData({ ...formData, registered_address_borrower: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Guarantor 1 Address</label>
                                    <textarea rows="2" value={formData.registered_address_guarantor_1} onChange={(e) => setFormData({ ...formData, registered_address_guarantor_1: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Guarantor 2 Address</label>
                                    <textarea rows="2" value={formData.registered_address_guarantor_2} onChange={(e) => setFormData({ ...formData, registered_address_guarantor_2: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Guarantor 3 Address</label>
                                    <textarea rows="2" value={formData.registered_address_guarantor_3} onChange={(e) => setFormData({ ...formData, registered_address_guarantor_3: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>

                                {/* Asset Details */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Outstanding Amount</label>
                                    <input type="text" value={formData.outstanding_amount} onChange={(e) => setFormData({ ...formData, outstanding_amount: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="Rs. 20,83,000" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Classification</label>
                                    <input type="text" value={formData.asset_classification} onChange={(e) => setFormData({ ...formData, asset_classification: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="e.g. NPA" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Date of Classification</label>
                                    <input type="text" value={formData.date_of_asset_classification} onChange={(e) => setFormData({ ...formData, date_of_asset_classification: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="e.g. 4-Sept-24" />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Details of Security Possessed</label>
                                    <textarea rows="2" value={formData.details_of_security_possessed} onChange={(e) => setFormData({ ...formData, details_of_security_possessed: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Name of Title Holder</label>
                                    <input type="text" value={formData.name_of_title_holder} onChange={(e) => setFormData({ ...formData, name_of_title_holder: e.target.value })} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                                </div>

                                {/* Meta */}
                                <div className="flex items-center gap-6 md:col-span-3">
                                    <div className="flex items-center gap-3">
                                        <input type="checkbox" id="isActive" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-5 h-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500" />
                                        <label htmlFor="isActive" className="text-sm font-semibold text-slate-700">Display Active</label>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="text-sm font-semibold text-slate-700">Display Order</label>
                                        <input type="number" value={formData.display_order} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })} className="w-20 px-3 py-1.5 border border-slate-200 rounded-lg outline-none text-sm" />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 mt-8">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-all">Cancel</button>
                                <button type="submit" disabled={submitting} className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50">
                                    {submitting ? 'Saving...' : 'Save Entry'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog isOpen={deleteConfirm.isOpen} onClose={() => setDeleteConfirm({ isOpen: false, id: null })} onConfirm={confirmDelete} title="Delete Entry" message="Are you sure you want to delete this secured asset entry?" />
        </div>
    );
};

export default SecuredAssetsTablePage;
